import CustomDialog from 'components/common/custom-dialog';
import { CustomOutlinePrintButton } from 'components/common/buttons/custom-outline-print-button';

import {
  SOAlertDetail,
  SOBannerDetail,
  SOCancelDialog,
  SOClientDetail,
  SODetailSkeleton,
  SOHeaderDetail,
  SOListError,
  SOProductDetail,
  SOTotalDetail,
} from '../../components';
import { ScheduledOrderInvoicePortal } from '../../print';

import {
  useScheduledOrderDetailDialog,
  useScheduledOrderDetailDialogLang,
} from './hooks';

import makeClasses from './ScheduledOrderDetailDialog.style';

interface Props {
  orderId: number | null;
  onClose: () => void;
}

export function ScheduledOrderDetailDialog({ orderId, onClose }: Props) {
  const classes = makeClasses();
  const lang = useScheduledOrderDetailDialogLang();

  const {
    isLoading,
    isError,
    data,
    refetch,
    isOpen,
    isMdUp,
    isCancelOpen,
    handlePrint,
    handleClose,
    openCancel,
    closeCancel,
    closeAfterCancel,
  } = useScheduledOrderDetailDialog({ orderId, onClose });

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={classes.stateWrapper} aria-label={lang.loadingLabel}>
          <SODetailSkeleton />
        </div>
      );
    }

    if (isError) {
      return (
        <div className={classes.stateWrapper}>
          <SOListError variant='detail' onRetry={() => refetch()} />
        </div>
      );
    }

    if (!data) {
      return null;
    }

    return (
      <div className={classes.layout}>
        <div className={classes.productsArea}>
          <SOHeaderDetail productsCount={data.products.length} />
          <SOAlertDetail />
          <SOProductDetail products={data.products} />
        </div>
        <div className={classes.totalArea}>
          <SOTotalDetail subtotal={data.subtotal} />
        </div>
        <div className={classes.mainArea}>
          <SOClientDetail clientName={data.clientName} />
          <SOBannerDetail placeAt={data.placeAt} />
          <button
            type='button'
            className={classes.cancelButton}
            onClick={openCancel}
          >
            {lang.cancelOrder}
          </button>
        </div>
      </div>
    );
  };

  const printButton = (
    <div className={classes.headerActions}>
      <CustomOutlinePrintButton
        onClick={handlePrint}
        classes={{
          button: !data ? classes.printButtonDisabled : undefined,
        }}
      />
    </div>
  );

  return (
    <>
      <CustomDialog
        classes={{
          root: classes.root,
          backdrop: classes.backdrop,
          paper: classes.paper,
          title: classes.title,
          headerRoot: classes.headerRoot,
        }}
        open={isOpen}
        title={`#${orderId}`}
        showBackButton={!isMdUp}
        showCloseButton={isMdUp}
        onClickBackButton={handleClose}
        onClose={handleClose}
        CustomDialogHeaderChildren={printButton}
      >
        {renderBody()}
      </CustomDialog>
      <ScheduledOrderInvoicePortal detail={data ?? null} />
      {orderId != null && (
        <SOCancelDialog
          orderId={orderId}
          open={isCancelOpen}
          onClose={closeCancel}
          onCanceled={closeAfterCancel}
          onFailed={closeAfterCancel}
        />
      )}
    </>
  );
}
